/**
 * Kusto (Azure Data Explorer) connection-string parser → kusto. It reads a string EXACTLY as the installed Kusto SDK does
 * (`kustoConnectionItems`, the azure-kusto-data 7.2.0 mirror — Option C C3 review r3): split on `;`, each item on `=`,
 * keywords case-insensitive with the SDK's aliases, a later alias of a property overriding an earlier one (C3-16), no
 * quoting, and a first item without `=` taken as the Data Source — Microsoft's URI-prefix form:
 *
 *   Data Source=https://help.kusto.windows.net;Initial Catalog=Samples;Fed=True
 *   https://help.kusto.windows.net/Samples;Fed=True;AppClientId=…;AppKey=…;AuthorityId=<tenant>
 *
 * Mapping: the Data Source → Cluster URL (and its one path segment → Database, unless an Initial Catalog names one —
 * `kustoAddress`), Fed → Microsoft Entra ID (Interactive), AppClientId / AppKey / Authority Id → Service Principal (the
 * key masked like every secret). Keywords the SDK does not know are listed as not used; empty items (a trailing `;`) are
 * skipped. Refused with a reason, filling nothing, when the SDK would not read the string as it looks: a value in quotes
 * or braces (the SDK keeps the marks and splits inside them — C3-14), a value holding `=` (the SDK cuts it off), or an
 * address `kustoAddress` cannot read completely.
 *
 * Whether a string IS Kusto is decided before this runs (`classifyKeywordString`, or the URI it starts with).
 */
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { KUSTO_KEYWORDS, kustoBuilderProperties, kustoConnectionItems, kustoPropertyOf } from '@/core/engines/kustoConnectionString';
import type { ConnectionStringParser, ParserMatch } from './types';
import { KUSTO_ADDRESS_PROBLEMS, isKustoHost, kustoAddress, kustoQuoteProblem, startsWithScheme, type KustoProblem } from './kustoAddress';
import { isPlaceholder } from './placeholders';


/** What the preview lists for a keyword that carries a secret the form has no field for (never its name or value). */
export const KUSTO_SECRET_LEFT_OUT = 'a token, key or password the form has no field for';

/** A keyword whose value is a secret: the SDK's secret properties, or a name that says token, key, secret, password… */
function isKustoSecretKey(key: string): boolean {
  const property = kustoPropertyOf(key);
  if (property) return KUSTO_KEYWORDS[property].isSecret === true;
  return /token|key|secret|password|pwd|cert/iu.test(key);
}

function refused(problem: KustoProblem): ParserMatch {
  return { format: 'kusto', engine: 'kusto', values: {}, matchedFields: [], unmatchedKeys: [], problem };
}

/** A keyword only a Kusto string carries (Fed, AppClientId, AppKey, Authority Id …, or a .NET-only token keyword). */
const KUSTO_ONLY = /^(aad federated security|federated security|federated|fed|aadfed|aad user id|application client id|appclientid|application key|appkey|application certificate .*|sendx5c|authority id|authorityid|authority|tenantid|tenant|tid|application token|apptoken|user token|usertoken|usrtoken|application certificate thumbprint|appcert)$/u;

export function namesKustoOnlyKeyword(text: string): boolean {
  return kustoConnectionItems(text).some((item) => KUSTO_ONLY.test(item.key.trim().toLowerCase()));
}

/**
 * A string that STARTS with a Kusto address — a URL whose host is a Kusto cluster or proxy, or a Kusto host before the
 * first `;` with no `=` (the SDK then reads that item as the Data Source). A URL whose first item carries a query,
 * fragment or user name is still Kusto's — and refused, never read as keywords.
 */
export function startsWithKustoAddress(text: string): boolean {
  const head = (text.split(';')[0] ?? '').trim();
  if (startsWithScheme(head)) return isKustoHost(head);
  return !head.includes('=') && isKustoHost(head);
}

export const parseKusto: ConnectionStringParser = (input) => {
  const text = input.trim();
  const head = (text.split(';')[0] ?? '').trim();
  if (startsWithScheme(head) && /[?#@=\s]/u.test(head)) return refused(KUSTO_ADDRESS_PROBLEMS.extras);

  const values: Partial<ConnectionFormValues> = { engine: 'kusto' };
  const unmatchedKeys: string[] = [];
  // A keyword the form has no field for is listed by name — unless it names a secret, which is never shown (C3-19).
  const leaveOut = (key: string): void => {
    const shown = isKustoSecretKey(key) ? KUSTO_SECRET_LEFT_OUT : key;
    if (!unmatchedKeys.includes(shown)) unmatchedKeys.push(shown);
  };
  let source: string | undefined;
  let catalog: string | undefined;
  let federated: { on: boolean; key: string } | undefined;
  let clientId: string | undefined;
  let clientKey: string | undefined;
  let tenant: string | undefined;

  const items = kustoConnectionItems(text).filter((item) => item.key.trim() || item.pieces > 1);
  // C3-19: the SDK never honours quoting — a quote or brace anywhere in any item, keyword known or not, refuses the string;
  // except a secret that is entirely a portal placeholder (`AppKey={your_password}`): not given (placeholders.ts).
  const placeholder = (item: (typeof items)[number]) => isKustoSecretKey(item.key.trim()) && isPlaceholder('password', item.value);
  const quoted = items.find((item) => /["'{}]/u.test(item.text) && !placeholder(item));
  if (quoted) return refused(kustoQuoteProblem(/["'{}]/u.exec(quoted.text)?.[0] ?? '"', quoted.property === 'dataSource'));
  // C3-20: the SDK drops one trailing '/' or '\' from the whole string — refused when that would cut a secret.
  if (/[/\\]$/u.test(text) && isKustoSecretKey(text.split(';').pop()?.split('=')[0] ?? '')) return refused(KUSTO_ADDRESS_PROBLEMS.cutSecret);
  // C3-21: read only what the SDK reads — an unknown keyword, an empty item or a flag with no value makes it throw.
  if (!kustoBuilderProperties(text)) return refused(KUSTO_ADDRESS_PROBLEMS.unreadable);

  for (const item of items) {
    const value = item.value?.trim();
    if (!item.property) {
      leaveOut(item.key.trim());
      continue;
    }
    if (item.pieces > 2) return refused(item.property === 'dataSource' ? KUSTO_ADDRESS_PROBLEMS.properties : KUSTO_ADDRESS_PROBLEMS.cutValue);
    switch (item.property) {
      case 'dataSource': source = value; break;
      case 'initialCatalog': catalog = value; break;
      case 'aadFederatedSecurity': federated = { on: value?.toLowerCase() === 'true', key: item.key.trim() }; break;
      case 'applicationClientId': clientId = value; break;
      case 'applicationKey': clientKey = value; break;
      case 'authorityId': tenant = value; break;
      default: leaveOut(item.key.trim());
    }
  }

  const matchedFields: string[] = [];
  if (source !== undefined) {
    const address = kustoAddress(source);
    if ('problem' in address) return refused(address.problem);
    values.server = address.server;
    matchedFields.push('server');
    if (address.database && !catalog) {
      values.database = address.database;
      matchedFields.push('database');
    }
  }
  if (catalog) {
    values.database = catalog;
    matchedFields.push('database');
  }
  if (clientId !== undefined) {
    values.login = clientId;
    matchedFields.push('user');
  }
  if (clientKey !== undefined) {
    values.password = clientKey;
    matchedFields.push('password');
  }
  if (tenant !== undefined) {
    values.entraTenantId = tenant;
    matchedFields.push('tenant');
  }
  if (federated && !federated.on) unmatchedKeys.push(federated.key);
  const servicePrincipal = clientId !== undefined || clientKey !== undefined;
  if (servicePrincipal || federated?.on) {
    values.authentication = servicePrincipal ? 'Service Principal' : 'Microsoft Entra ID (Interactive Browser)';
    matchedFields.push('authentication');
  }
  return { format: 'kusto', engine: 'kusto', values, matchedFields, unmatchedKeys };
};
